import { createFileRoute, Link } from "@tanstack/react-router";
import { Bell, ChevronRight, MapPin, Sparkles, Flame } from "lucide-react";
import { useEffect, useState } from "react";
import {
  listTemplesSummary,
  listVisitTempleIds,
  getProfile,
  listActiveAnnouncementTitles,
} from "@/lib/yatra-db";
import { useAuth } from "@/lib/auth";
import { useI18n, pickName } from "@/lib/i18n";
import { jainArt, type JainArtKey } from "@/components/jain-icon";
const mandala = jainArt.mandala;

type Temple = {
  id: string;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  yatra_order: number;
  mool_nayak: string;
};
type Profile = { full_name: string | null; phone: string | null };

export const Route = createFileRoute("/app/")({ component: AppHome });

function AppHome() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [temples, setTemples] = useState<Temple[]>([]);
  const [visited, setVisited] = useState<Set<string>>(new Set());
  const [profile, setProfile] = useState<Profile | null>(null);
  const [ann, setAnn] = useState<
    Array<{ id: string; title_en: string; title_hi: string; title_gu: string }>
  >([]);

  useEffect(() => {
    if (!user) {
      return;
    }
    (async () => {
      const tps = listTemplesSummary();
      const vs = listVisitTempleIds(user.id);
      const pr = getProfile(user.id);
      const an = listActiveAnnouncementTitles(3);
      setTemples(tps);
      setVisited(new Set(vs));
      setProfile(pr ? { full_name: pr.full_name, phone: pr.phone } : null);
      setAnn(an);
    })();
  }, [user]);

  const total = temples.length || 31;
  const visitedCount = visited.size;
  const next = temples.find((tp) => !visited.has(tp.id));
  const displayName = profile?.full_name || profile?.phone || "Yatri";
  const pct = Math.round((visitedCount / total) * 100);

  const TILES: Array<{ art: JainArtKey; label: string; to: string; sub: string }> = [
    { art: "derasar", label: t("temples"), to: "/app/temples", sub: `${total} ${t("temples")}` },
    { art: "lotus", label: t("map"), to: "/app/map", sub: t("navigate") },
    { art: "ahimsa", label: t("scanQr"), to: "/app/scan", sub: t("checkIn") },
    {
      art: "kalash",
      label: t("certificate"),
      to: "/app/certificate",
      sub: `${visitedCount}/${total}`,
    },
    { art: "namaste", label: t("bookRickshaw"), to: "/app/bookings", sub: t("bookNow") },
    { art: "diya", label: "Updates", to: "/app/announcements", sub: `${ann.length} live` },
  ];

  // SVG ring metrics
  const R = 38,
    C = 2 * Math.PI * R;
  const dash = (pct / 100) * C;

  return (
    <div>
      {/* HERO */}
      <div
        className="relative overflow-hidden rounded-b-[2.5rem] px-6 pb-10 pt-16 text-primary-foreground animate-shimmer"
        style={{ background: "var(--gradient-sunset)", backgroundSize: "200% 200%" }}
      >
        {/* decorative mandala */}
        <img
          src={mandala}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-16 h-72 w-72 opacity-25 mix-blend-screen animate-spin-slow"
        />
        <img
          src={mandala}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 opacity-15 mix-blend-screen animate-spin-slow"
          style={{ animationDirection: "reverse" }}
        />

        <div className="relative">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] opacity-95">
            <Flame className="h-3 w-3 animate-flicker" /> 🙏 {t("welcome")}
          </div>
          <div className="mt-1 font-serif text-3xl font-bold leading-tight drop-shadow-sm">
            {displayName}
          </div>
          <div className="text-[11px] opacity-85">Jain Gaurav Yatra · Ahmedabad</div>

          {/* Progress card with circular ring */}
          <div className="mt-5 flex items-center gap-4 rounded-2xl glass p-4 text-foreground shadow-[0_10px_30px_-12px_rgba(0,0,0,0.3)]">
            <div className="relative h-[88px] w-[88px] shrink-0">
              <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                <circle
                  cx="50"
                  cy="50"
                  r={R}
                  fill="none"
                  stroke="color-mix(in oklab, hsl(var(--saffron)) 20%, transparent)"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r={R}
                  fill="none"
                  stroke="url(#g1)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${dash} ${C}`}
                  style={{ transition: "stroke-dasharray 1s ease" }}
                />
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--saffron))" />
                    <stop offset="100%" stopColor="hsl(var(--saffron-deep))" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-serif text-xl font-bold text-gradient">{pct}%</span>
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                  done
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-muted-foreground">{t("progress")}</div>
              <div className="mt-0.5 font-serif text-lg font-bold">
                {visitedCount} / {total}
              </div>
              <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-cream-deep px-2 py-0.5 text-[10px] font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> {total - visitedCount} {t("pending")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5">
        {/* AI Assistant — Highlighted */}
        <Link
          to="/app/ai"
          className="mt-4 mb-3 flex items-center gap-3 overflow-hidden rounded-2xl p-[2px] shadow-[0_10px_30px_-10px_color-mix(in_oklab,hsl(var(--saffron))_55%,transparent)] animate-shimmer"
          style={{
            background:
              "linear-gradient(120deg, hsl(var(--saffron)), hsl(var(--saffron-deep)), hsl(var(--saffron)))",
            backgroundSize: "200% 200%",
          }}
        >
          <div className="flex w-full items-center gap-3 rounded-[14px] bg-card px-3.5 py-3">
            <div
              className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-primary-foreground"
              style={{ background: "var(--gradient-saffron)" }}
            >
              <Sparkles className="h-5 w-5 animate-flicker" />
              <span className="absolute -right-1 -top-1 rounded-full bg-maroon px-1.5 py-[1px] text-[8px] font-bold text-white">
                AI
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                  ✨ New · AI Powered
                </span>
              </div>
              <div className="font-serif text-[15px] font-bold leading-tight">Yatra Sahayak</div>
              <div className="text-[11px] text-muted-foreground">
                Ask anything · temples, aarti, dharma
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-primary" />
          </div>
        </Link>

        {next && (
          <Link
            to="/app/temples/$id"
            params={{ id: next.id }}
            className="block overflow-hidden rounded-2xl border border-[color-mix(in_oklab,hsl(var(--saffron))_30%,transparent)] bg-card p-4 shadow-[var(--shadow-soft)] tile-shine animate-scale-pop"
          >
            <div className="flex items-start gap-3">
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-primary-foreground ring-glow"
                style={{ background: "var(--gradient-saffron)" }}
              >
                <span className="font-serif text-base font-bold">#{next.yatra_order}</span>
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-primary">
                  {t("nextTemple")}
                </div>
                <div className="font-serif text-base font-bold leading-tight">
                  {pickName(next, lang)}
                </div>
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {next.area}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </Link>
        )}

        <div className="mt-5 grid grid-cols-3 gap-2.5 stagger">
          {TILES.map((tile) => (
            <Link
              key={tile.label}
              to={tile.to}
              className="tile-shine group rounded-2xl border border-border bg-card p-3 text-center transition-all hover:-translate-y-1 hover:shadow-[var(--shadow-warm)] active:scale-95"
            >
              <div
                className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl transition-transform group-hover:scale-110 group-hover:rotate-[-6deg]"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, color-mix(in oklab, hsl(var(--saffron)) 20%, transparent), transparent 70%)",
                }}
              >
                <img
                  src={jainArt[tile.art]}
                  alt=""
                  aria-hidden
                  className="h-12 w-12 object-contain drop-shadow-sm"
                  loading="lazy"
                />
              </div>
              <div className="mt-1 font-serif text-[12px] font-bold leading-tight text-foreground">
                {tile.label}
              </div>
              <div className="text-[10px] text-muted-foreground">{tile.sub}</div>
            </Link>
          ))}
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-base font-bold">Recent {t("temples")}</h3>
            <Link
              to="/app/temples"
              className="flex items-center gap-0.5 text-xs font-semibold text-primary hover:gap-1.5 transition-all"
            >
              See all <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="mt-3 space-y-2 stagger">
            {temples.slice(0, 4).map((tp) => {
              const done = visited.has(tp.id);
              return (
                <Link
                  key={tp.id}
                  to="/app/temples/$id"
                  params={{ id: tp.id }}
                  className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]"
                >
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl font-serif font-bold text-primary-foreground shadow-md"
                    style={{ background: "var(--gradient-saffron)" }}
                  >
                    {tp.yatra_order}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold leading-tight">{pickName(tp, lang)}</div>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {tp.area}
                    </div>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${done ? "bg-green-100 text-green-700" : "bg-primary/12 text-primary"}`}
                  >
                    {done ? "✓ " + t("visited") : t("pending")}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>

        {ann.length > 0 && (
          <div className="mt-5 rounded-2xl border border-border bg-gradient-to-br from-cream to-cream-deep p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-3.5 w-3.5 text-primary" />
                <h3 className="font-serif text-sm font-bold">Feed · Announcements</h3>
              </div>
              <Link
                to="/app/announcements"
                className="flex items-center gap-0.5 text-[11px] font-semibold text-primary hover:gap-1.5 transition-all"
              >
                See all <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="space-y-1.5">
              {ann.map((a) => (
                <Link
                  key={a.id}
                  to="/app/announcements"
                  className="block rounded-xl bg-card px-3 py-2 text-xs font-medium shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle animate-flicker" />
                  {pickName(
                    { name_en: a.title_en, name_hi: a.title_hi, name_gu: a.title_gu } as any,
                    lang,
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="h-6" />
      </div>
    </div>
  );
}
